import { useState, useRef, useEffect } from 'react';
import { Camera, Trash2, Sliders, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ImageCropAdjustModal } from './ImageCropAdjustModal';
import { filesApi } from '@/api/http-client';

export interface CoverPhotoUploaderProps {
  currentUrl?: string | null;
  onCoverChange: (urlOrKey: string) => void;
  className?: string;
  ownerId?: string;
}

/**
 * Componente de Imagem de Capa do Médico (Estilo Facebook / Banner Panorâmico)
 * com suporte a upload, preview, ajuste de enquadramento (crop/zoom/rotate) e remoção.
 */
export function CoverPhotoUploader({
  currentUrl,
  onCoverChange,
  className = '',
  ownerId,
}: CoverPhotoUploaderProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentUrl || null);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPreviewUrl(currentUrl || null);
  }, [currentUrl]);

  const handleSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setCropImageSrc(result);
      setIsCropModalOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleApplyCropped = async (croppedBase64: string) => {
    setPreviewUrl(croppedBase64);
    if (ownerId) {
      setIsUploading(true);
      try {
        const res = await fetch(croppedBase64);
        const blob = await res.blob();
        const croppedFile = new File([blob], 'cover_photo.jpg', { type: 'image/jpeg' });
        const uploaded = await filesApi.uploadFile(
          croppedFile,
          ownerId,
          'DOCTOR',
          'PROFILE_PHOTO',
          'PUBLIC',
        );
        const finalUrl = uploaded.url || (uploaded.key ? filesApi.getFileUrl(uploaded.key) : uploaded.id);
        setPreviewUrl(finalUrl);
        onCoverChange(uploaded.key || uploaded.url || uploaded.id);
        return;
      } catch (err) {
        console.warn('Erro ao enviar imagem de capa recortada para o storage, usando preview:', err);
      } finally {
        setIsUploading(false);
      }
    }
    onCoverChange(croppedBase64);
  };

  const handleRemoveCover = () => {
    setPreviewUrl(null);
    onCoverChange('');
  };

  const displayUrl = previewUrl
    ? previewUrl.startsWith('http') || previewUrl.startsWith('data:')
      ? previewUrl
      : filesApi.getFileUrl(previewUrl)
    : null;

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center justify-between">
        <label className="block text-sm font-semibold text-foreground">
          Imagem de Capa do Perfil (Estilo Panorâmico)
        </label>
        <span className="text-xs text-muted-foreground">Recomendado: 1200 x 400 px</span>
      </div>

      <div className="relative group w-full h-44 sm:h-52 rounded-2xl border border-border overflow-hidden bg-muted/40 shadow-xs flex items-center justify-center">
        {displayUrl ? (
          <img
            src={displayUrl}
            alt="Capa do Perfil"
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.01]"
          />
        ) : (
          <div className="text-center p-6 space-y-2">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto shadow-xs">
              <ImageIcon className="h-6 w-6" />
            </div>
            <p className="text-xs font-semibold text-foreground">Nenhuma imagem de capa selecionada</p>
            <p className="text-[11px] text-muted-foreground">
              Adicione uma foto de capa personalizada para destacar seu perfil na vitrine e nos agendamentos.
            </p>
          </div>
        )}

        {isUploading && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center gap-2 text-white z-10 backdrop-blur-xs">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-xs font-medium">Enviando capa...</span>
          </div>
        )}

        {/* Overlay com Ações de Upload e Ajuste */}
        <div className="absolute inset-0 bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2.5 backdrop-blur-xs">
          <Button
            type="button"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="rounded-xl text-xs font-bold gap-1.5 h-10 px-4 bg-white hover:bg-white/90 text-gray-900 shadow-md cursor-pointer"
          >
            <Camera className="h-4 w-4" />
            <span>{displayUrl ? 'Alterar Capa' : 'Adicionar Capa'}</span>
          </Button>

          {displayUrl && (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={isUploading}
                onClick={() => {
                  setCropImageSrc(displayUrl);
                  setIsCropModalOpen(true);
                }}
                className="rounded-xl text-xs font-semibold gap-1.5 h-10 px-3.5 bg-black/50 hover:bg-black/70 text-white border-white/20 backdrop-blur-xs cursor-pointer"
                title="Ajustar enquadramento e zoom"
              >
                <Sliders className="h-4 w-4" />
                <span>Ajustar</span>
              </Button>

              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={isUploading}
                onClick={handleRemoveCover}
                className="rounded-xl text-xs font-semibold gap-1.5 h-10 px-3.5 bg-red-600/80 hover:bg-red-600 text-white border-transparent cursor-pointer"
                title="Remover foto de capa"
              >
                <Trash2 className="h-4 w-4" />
                <span>Remover</span>
              </Button>
            </>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleSelectFile}
          className="hidden"
        />
      </div>

      {/* Modal de Crop e Ajuste Panorâmico (Aspect Ratio 12/4 = 3/1) */}
      {cropImageSrc && (
        <ImageCropAdjustModal
          imageSrc={cropImageSrc}
          isOpen={isCropModalOpen}
          onClose={() => setIsCropModalOpen(false)}
          onApplyCrop={handleApplyCropped}
          aspectRatio={12 / 4}
          title="Ajustar Imagem de Capa do Perfil"
          isCircle={false}
        />
      )}
    </div>
  );
}
